import React, { useState } from 'react';
import { MBView } from '@shared/components/MBView';
import { MBHoverProps } from './props';

/*
    Wrapper component that listens to mouse hover state. Different props
    exist to act on hover events. Eg render overlays, callbacks etc.
    Useful for crafting hover experiences without needing too much state.


    The 2 basic use cases:

    <MBHover hoverContent={<ChildredThatShouldShowOnHover />}>
      <ChildrenThatShouldBeHovered />
    </MBHover>

    or

    <MBHover
      onHoverStart={() => setLocalHoveredState(true)}
      onHoverEnd={() => setLocalHoveredState(false)}
    >
      <ChildrenThatShouldBeHovered />
    </MBHover>

    Note: it's important that hoverContent is inside the hovering
    div, otherwise onMouseLeave would be called when hovering that
    content, even though it might be absolutely positioned intop
    of the actual div.

    Note: it's very important that
    - content that should NOT be rerendered on hover be passed as children (NOT a function component that takes a `hovering` prop)
    - content that SHOULD be rendered on hover be passed as `hoverContent`
*/
export function MBHover({
  onHoverStart,
  onHoverEnd,
  style,
  hoverContent,
  hoverStyle,
  hoverContainerStyle,
  children,
  ...otherProps
}: MBHoverProps) {
  const [isHovered, setIsHovered] = useState<boolean>(false);

  const onMouseEnter = () => {
    setIsHovered(true);
    onHoverStart?.();
  };

  const onMouseLeave = () => {
    setIsHovered(false);
    onHoverEnd?.();
  };

  return (
    <div style={hoverContainerStyle} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <MBView {...otherProps} style={[style, isHovered && hoverStyle]}>
        {children}
        {isHovered && hoverContent}
      </MBView>
    </div>
  );
}
